import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { apiBase } from "./api";

// One Socket.IO connection to the /helpdesk namespace per browser tab, shared
// by every helpdesk screen. Reconnects on its own; screens re-fetch with
// `?after=<lastId>` on "reconnect" so nothing is missed while offline.

const socketUrl = () => (process.env.REACT_APP_HELPDESK_SOCKET_URL || apiBase()).replace(/\/+$/, "");
const socketPath = () => process.env.REACT_APP_HELPDESK_SOCKET_PATH || "/socket.io";

let agentSocket = null;
let tokenUsed = null;

export const getAgentSocket = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  if (agentSocket && tokenUsed === token) return agentSocket;
  if (agentSocket) agentSocket.close();
  tokenUsed = token;
  agentSocket = io(`${socketUrl()}/helpdesk`, {
    path: socketPath(),
    auth: { token },
    transports: ["websocket", "polling"],
    reconnectionDelayMax: 10000,
  });
  return agentSocket;
};

export const closeAgentSocket = () => {
  if (agentSocket) agentSocket.close();
  agentSocket = null;
  tokenUsed = null;
};

/** Subscribe to one or more events for the lifetime of the component. */
export const useSocketEvent = (events, handler) => {
  const ref = useRef(handler);
  ref.current = handler;
  const key = [].concat(events).join("|");
  useEffect(() => {
    const s = getAgentSocket();
    if (!s) return undefined;
    const list = key.split("|");
    const fns = list.map((ev) => {
      const fn = (payload) => ref.current(payload, ev);
      s.on(ev, fn);
      return [ev, fn];
    });
    return () => fns.forEach(([ev, fn]) => s.off(ev, fn));
  }, [key]);
};

/** Join a room (ticket / conversation) while mounted. */
export const useRoom = (kind, id) => {
  useEffect(() => {
    const s = getAgentSocket();
    if (!s || !id) return undefined;
    const join = () => s.emit(`${kind}.join`, { [`${kind}Id`]: id }, () => {});
    join();
    s.on("connect", join); // rejoin after reconnect
    return () => {
      s.off("connect", join);
      s.emit(`${kind}.leave`, { [`${kind}Id`]: id });
    };
  }, [kind, id]);
};

export const createVisitorSocket = (token) =>
  io(`${socketUrl()}/widget`, { path: socketPath(), auth: { token }, transports: ["websocket", "polling"], reconnectionDelayMax: 10000 });
